import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useInfiniteQuery } from '@tanstack/react-query';
import { createColumnHelper, flexRender, getCoreRowModel, SortingState, useReactTable } from '@tanstack/react-table';

import { Dimensions, Sources } from 'components/insights/constants';
import ReferralApiExport from 'components/insights/referrals/ReferralApiExport';
import CircularProgress from 'components/shared/CircularProgress';
import {
  DataTableTheme,
  Table as StyledTable,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderFooterRow,
  TableRow,
} from 'components/shared/DataTable';
import ExpandableArea from 'components/shared/ExpandableArea';
import useIntersectionObserver from 'hooks/useIntersectionObserver';
import { simpleDate } from 'lib/date';
import { Analytics, retrieveAnalytics } from 'services/api/insights/analytics';
import { serializeSort } from 'stores/helpers';
import {
  getReferralAnalyticsFilters,
  getSlideoutEntityFilter,
  useReferralsInsightsStore,
} from 'stores/referralsInsightsStore';
import ArrowUpDownIcon from 'svg/ArrowUpDownIcon';

type ReferralRow = {
  reviewId: string;
  episodeId: string;
  patientName: string;
  referredBy: string;
  referredTo: string;
  createdAt: string;
};

const columnHelper = createColumnHelper<ReferralRow>();

const columns = [
  columnHelper.accessor('patientName', {
    id: Dimensions.PATIENT_NAME,
    header: 'Patient Name',
    cell: (info) => {
      const name = info.getValue();
      const { episodeId, reviewId } = info.row.original;
      if (!name) return '—';
      if (!episodeId || !reviewId) return name;
      // Deep link into the patient story at the specific referral.
      return (
        <ReferralLink href={`/patients/${episodeId}/referrals/${reviewId}`} target='_blank' rel='noopener noreferrer'>
          {name}
        </ReferralLink>
      );
    },
    enableSorting: true,
  }),
  columnHelper.accessor('referredBy', {
    id: Dimensions.SENDING_GROUP,
    header: 'Referred By',
    cell: (info) => info.getValue() || '—',
    enableSorting: true,
  }),
  columnHelper.accessor('referredTo', {
    id: Dimensions.RECEIVING_GROUP,
    header: 'Referred To',
    cell: (info) => info.getValue() || '—',
    enableSorting: true,
  }),
  columnHelper.accessor('createdAt', {
    id: Dimensions.DATE,
    header: 'Referral Created Date',
    cell: (info) => (info.getValue() ? simpleDate(info.getValue()) : '—'),
    enableSorting: true,
  }),
];

const QUERY_DIMENSIONS = [
  Dimensions.REVIEW_ID,
  Dimensions.EPISODE_ID,
  Dimensions.PATIENT_NAME,
  Dimensions.SENDING_GROUP,
  Dimensions.RECEIVING_GROUP,
  Dimensions.DATE,
];

const METRIC_STATUS_FILTER: Record<string, string | undefined> = {
  denial_rate: 'rejected',
  acceptance_rate: 'accepted',
  response_time: 'accepted,rejected',
};

// REVIEW_ID and EPISODE_ID exist in the in-table query only to support the deep-link href —
// omitted here so the CSV columns match the visible table columns. REFERRAL_URL replaces them
// in the CSV with a single user-friendly link to the referral.
const EXPORT_DIMENSIONS = [
  Dimensions.PATIENT_NAME,
  Dimensions.SENDING_GROUP,
  Dimensions.RECEIVING_GROUP,
  Dimensions.DATE,
  Dimensions.REFERRAL_URL,
];

const METRIC_EXPORT_SLUGS: Record<string, string> = {
  denial_rate: 'denial-rate',
  acceptance_rate: 'acceptance-rate',
  response_time: 'response-time',
};

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function PatientListSection() {
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);

  const exportParams = useMemo(
    () => ({
      source: Sources.INSIGHTS_REFERRALS,
      dimensions: EXPORT_DIMENSIONS,
      metrics: [],
    }),
    []
  );

  // Mirror of the in-section query's filter composition so the CSV row set matches the table.
  const exportExtraFilters = useMemo(() => {
    if (!slideoutContext) return null;
    const entityFilter = getSlideoutEntityFilter(slideoutContext);
    const statusFilter = slideoutContext.kind === 'metric' ? METRIC_STATUS_FILTER[slideoutContext.metric] : undefined;
    return {
      ...(entityFilter ?? {}),
      ...(statusFilter ? { referralStatus: statusFilter } : {}),
    };
  }, [slideoutContext]);

  const exportFilename = useMemo(() => {
    if (!slideoutContext) return undefined;
    const suffix =
      slideoutContext.kind === 'metric'
        ? METRIC_EXPORT_SLUGS[slideoutContext.metric]
        : slugify(slideoutContext.entityName);
    return `referral-coordination-patients${suffix ? `-${suffix}` : ''}`;
  }, [slideoutContext]);

  // ExpandableArea's handle bar spans the full width of its parent — keep `Wrapper` unpadded.
  return (
    <Wrapper>
      <Header>
        <Title>Referrals</Title>
        <ReferralApiExport params={exportParams} extraFilters={exportExtraFilters} filename={exportFilename} />
      </Header>
      <ExpandableArea>
        <PatientListTable />
      </ExpandableArea>
    </Wrapper>
  );
}

// Split so the query is only fired once the user expands — ExpandableArea unmounts children
// when collapsed.
function PatientListTable() {
  const baseFilters = useReferralsInsightsStore((state) => getReferralAnalyticsFilters(state));
  const slideoutContext = useReferralsInsightsStore((state) => state.slideoutContext);

  const analyticsFilters = useMemo(() => {
    if (!baseFilters) return null;
    const statusFilter = slideoutContext?.kind === 'metric' ? METRIC_STATUS_FILTER[slideoutContext.metric] : undefined;
    const entityFilter = slideoutContext ? getSlideoutEntityFilter(slideoutContext) : null;
    return {
      ...baseFilters,
      ...(entityFilter ?? {}),
      ...(statusFilter ? { referralStatus: statusFilter } : {}),
    };
  }, [baseFilters, slideoutContext]);

  const [sort, setSort] = useState<SortingState>([{ id: Dimensions.DATE, desc: true }]);

  const queryParams = useMemo(
    () => ({
      source: Sources.INSIGHTS_REFERRALS,
      dimensions: QUERY_DIMENSIONS,
      metrics: [],
      sortBy: serializeSort(sort),
      pageSize: 10,
    }),
    [sort]
  );

  const query = useInfiniteQuery({
    queryKey: ['referral-slideout-patient-list', queryParams, analyticsFilters],
    queryFn: ({ pageParam }) =>
      retrieveAnalytics({ ...queryParams, ...((pageParam ?? {}) as Record<string, unknown>) }, analyticsFilters),
    enabled: !!analyticsFilters,
    initialPageParam: queryParams as Record<string, unknown>,
    getNextPageParam: (lastPage: Analytics) =>
      (lastPage?.links?.next?.payload as Record<string, unknown> | undefined) ?? null,
  });

  const rows = useMemo<ReferralRow[]>(() => {
    const pages = query.data?.pages ?? [];
    if (!pages.length) return [];
    const headers = pages[0]?.dimensionHeaders ?? [];
    const idx = (name: string) => headers.findIndex((h) => h.name === name);
    const reviewIdIdx = idx(Dimensions.REVIEW_ID);
    const episodeIdIdx = idx(Dimensions.EPISODE_ID);
    const patientNameIdx = idx(Dimensions.PATIENT_NAME);
    const sendingGroupIdx = idx(Dimensions.SENDING_GROUP);
    const receivingGroupIdx = idx(Dimensions.RECEIVING_GROUP);
    const dateIdx = idx(Dimensions.DATE);
    return pages.flatMap((page) =>
      (page?.data ?? []).map((row): ReferralRow => {
        const dimValue = (i: number) => (i >= 0 ? row.dimensionValues[i]?.value : undefined);
        return {
          reviewId: String(dimValue(reviewIdIdx) ?? ''),
          episodeId: String(dimValue(episodeIdIdx) ?? ''),
          patientName: String(dimValue(patientNameIdx) ?? ''),
          referredBy: String(dimValue(sendingGroupIdx) ?? ''),
          referredTo: String(dimValue(receivingGroupIdx) ?? ''),
          createdAt: String(dimValue(dateIdx) ?? ''),
        };
      })
    );
  }, [query.data]);

  const scrollAreaRef = useRef<HTMLDivElement | null>(null);
  const { customRef, isIntersecting } = useIntersectionObserver({ threshold: 0.2, root: scrollAreaRef.current });

  useEffect(() => {
    if (isIntersecting && query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage();
    }
  }, [isIntersecting, query]);

  const table = useReactTable({
    columns,
    data: rows,
    state: { sorting: sort },
    onSortingChange: (updater) => {
      const next = updater instanceof Function ? updater(sort) : updater;
      setSort(next);
    },
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
  });

  const showEmpty = !query.isLoading && rows.length === 0;
  const showNextSpinner = query.isFetchingNextPage;

  return (
    <ScrollArea ref={scrollAreaRef}>
      <StyledTable $theme={DataTableTheme.DEFAULT} $freezeFirstColumn>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableHeaderFooterRow key={headerGroup.id} $theme={DataTableTheme.DEFAULT}>
              {headerGroup.headers.map((header) => (
                <TableHeaderCell
                  key={header.id}
                  isSorted={!!header.column.getIsSorted()}
                  isSortable={header.column.getCanSort()}
                  onClick={header.column.getToggleSortingHandler()}
                  width={header.getSize()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanSort() && (
                    <ArrowUpDownIcon
                      direction={header.column.getIsSorted() || 'none'}
                      height={10}
                      width={10}
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </TableHeaderCell>
              ))}
            </TableHeaderFooterRow>
          ))}
        </TableHeader>
        <TableBody>
          {showEmpty && (
            <TableRow>
              <EmptyCell colSpan={columns.length}>No referrals found for this period.</EmptyCell>
            </TableRow>
          )}
          {table.getRowModel().rows.map((row) => (
            <TableRow key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} width={cell.column.getSize()}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
          <SentinelRow ref={customRef} />
          {showNextSpinner && (
            <TableRow>
              <SpinnerCell colSpan={columns.length}>
                <CircularProgress size={16} thickness={2} />
              </SpinnerCell>
            </TableRow>
          )}
        </TableBody>
      </StyledTable>
    </ScrollArea>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
`;

const ScrollArea = styled.div`
  max-height: 520px;
  overflow: auto;
  border: 1px solid ${({ theme }) => theme.colors.black10};
  border-radius: 4px;
`;

const Header = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 24px 24px 12px;
`;

const SentinelRow = styled.tr`
  display: block;
  height: 1px;
`;

const SpinnerCell = styled.td`
  display: flex;
  justify-content: center;
  padding: 12px;
  width: 100%;
  color: ${({ theme }) => theme.colors.primaryBlue};
`;

const Title = styled.h3`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.colors.black};
`;

const ReferralLink = styled.a`
  color: ${({ theme }) => theme.colors.primaryBlue};
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const EmptyCell = styled.td`
  padding: 24px;
  width: 100%;
  text-align: center;
  color: ${({ theme }) => theme.colors.black50};
`;
